import { Temporal } from "@js-temporal/polyfill";
import { z } from "zod";

/** A calendar-aware ISO-8601 duration represented by Temporal. */
export type Duration = Temporal.Duration;

const compactDurationPattern = /^([1-9]\d*)(s|m|h|d|w|mo|y)$/;

/**
 * The unit suffixes of Verdi's compact duration syntax, mapped to the {@link Temporal.Duration}
 * field each one populates. The single source of truth for which units the compact form supports —
 * callers that need to inspect a parsed duration's units (e.g. bucket widths) iterate this rather
 * than restating the list.
 */
export const durationFieldByUnit = {
    s: "seconds",
    m: "minutes",
    h: "hours",
    d: "days",
    w: "weeks",
    mo: "months",
    y: "years",
} as const;

/** A unit suffix accepted by Verdi's compact duration syntax. */
export type DurationUnit = keyof typeof durationFieldByUnit;

/** The {@link Temporal.Duration} field corresponding to a {@link DurationUnit}. */
export type DurationField = (typeof durationFieldByUnit)[DurationUnit];

/**
 * Parses Verdi's compact one-unit duration syntax or a full ISO-8601 duration. Unlike the
 * compact form, ISO-8601 here may combine multiple units — some callers (e.g. bucket widths)
 * additionally restrict this to a single unit themselves.
 *
 * @example
 * parseDuration("15m");     // compact syntax — always a single unit
 * parseDuration("PT1H30M"); // ISO-8601 — multiple units allowed here
 */
export function parseDuration(input: string): Duration {
    const compactMatch = compactDurationPattern.exec(input);
    if (compactMatch) {
        const [, countText, unit] = compactMatch;
        const count = Number(countText);
        if (!Number.isSafeInteger(count)) throw new RangeError("Duration count must be a safe integer");
        return Temporal.Duration.from({ [durationFieldByUnit[unit as DurationUnit]]: count });
    }

    return Temporal.Duration.from(input);
}

/** Canonical portable representation for a duration at API and PostgreSQL parameter boundaries. */
export function formatDuration(duration: Duration): string {
    return duration.toString();
}

function assertPostgresPrecision(duration: Duration): void {
    if (duration.nanoseconds !== 0) {
        throw new RangeError("PostgreSQL intervals support microsecond precision, not nanoseconds");
    }
}

/** PostgreSQL accepts ISO-8601 interval input, so no database-specific formatting is required. */
export function durationToPostgresInterval(duration: Duration): string {
    assertPostgresPrecision(duration);
    return formatDuration(duration);
}

/**
 * Parses PostgreSQL's default `postgres` IntervalStyle output into the corresponding Temporal duration.
 * PostgreSQL's mixed-sign and fractional-calendar intervals cannot be represented by Temporal.Duration
 * without changing their meaning, so those values are rejected.
 */
export function durationFromPostgresInterval(value: string): Duration {
    if (value.startsWith("P") || value.startsWith("+P") || value.startsWith("-P")) return parseDuration(value);

    const parts = {
        years: 0,
        months: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
        microseconds: 0,
    };
    let remaining = value.trim();

    const extract = (pattern: RegExp, field: "years" | "months" | "days") => {
        const match = pattern.exec(remaining);
        if (!match) return;
        parts[field] = Number(match[1]);
        remaining = remaining.replace(match[0], " ").trim();
    };

    extract(/([+-]?\d+)\s+years?\b/i, "years");
    extract(/([+-]?\d+)\s+mons?\b/i, "months");
    extract(/([+-]?\d+)\s+days?\b/i, "days");

    if (remaining) {
        const time = /^([+-]?)(\d+):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/.exec(remaining);
        if (!time) throw new RangeError(`Unsupported PostgreSQL interval: ${value}`);
        const [, signText, hours, minutes, seconds, fraction = ""] = time;
        const sign = signText === "-" ? -1 : 1;
        parts.hours = sign * Number(hours);
        parts.minutes = sign * Number(minutes);
        parts.seconds = sign * Number(seconds);
        const fractionMicros = Number(fraction.padEnd(6, "0"));
        parts.milliseconds = sign * Math.floor(fractionMicros / 1_000);
        parts.microseconds = sign * (fractionMicros % 1_000);
    }

    const signs = Object.values(parts)
        .filter((part) => part !== 0)
        .map(Math.sign);
    if (new Set(signs).size > 1) {
        throw new RangeError(
            "PostgreSQL intervals with mixed-sign fields are not representable as a Temporal.Duration",
        );
    }

    return Temporal.Duration.from(parts);
}

/** Zod parser for either compact Verdi duration syntax or a standard ISO-8601 duration string. */
export const DurationSchema = z.string().transform((input, context) => {
    try {
        return parseDuration(input);
    } catch (error) {
        context.addIssue({
            code: "custom",
            message: error instanceof Error ? error.message : "Invalid duration",
        });
        return z.NEVER;
    }
});
